'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  SearchApi,
  SearchApiControllerSearchSellerOrdersGroupStatusEnum,
  SearchApiControllerSearchSellerOrdersOrderEnum,
  SearchApiControllerSearchSellerOrdersSortByEnum,
} from '@ps/api/search';
import type { RichOrderItem } from '@ps/api/types';
import { config } from '@ps/config/client';
import { auth } from '@ps/functions/firebase/auth';
import { withClientAPI } from '@ps/functions/utils/api';

import type { OrderMode } from '../../../../orders/types';

const PAGE_SIZE = 20;

interface OrdersSearchResponseLike {
  total?: number;
  data?: { rows?: RichOrderItem[] };
}

export interface ShareableOrders {
  data: RichOrderItem[];
  isInitializing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Paginated orders shared between the two chat participants, scoped by
 * the sender's role (1:1 with PopshopRN's `shareOrder` criterion):
 *
 * - `seller` — orders the creator sold to this buyer:
 *   `searchSellerOrders({ storeId: currentUserId, buyerId: partnerId })`.
 * - `buyer` — the fan's purchases from this creator: the user-orders
 *   search (no typed method), filtered to `sellerId = partnerId`.
 *
 * A request-id guard drops out-of-order responses; sort is fixed to
 * `createdAt desc`. Mirrors `orders/hooks/useOrderList.ts`.
 */
export function useShareableOrders(
  role: OrderMode,
  currentUserId: string,
  partnerId: string,
): ShareableOrders {
  const [data, setData] = useState<RichOrderItem[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const requestId = useRef(0);

  const fetchPage = useCallback(
    async (page: number) => {
      const rid = ++requestId.current;
      if (page === 1) setIsInitializing(true);
      else setIsLoadingMore(true);
      try {
        const rows =
          role === 'seller'
            ? await fetchSellerOrders(currentUserId, partnerId, page)
            : await fetchBuyerOrders(currentUserId, partnerId, page);

        if (rid !== requestId.current) return; // stale response
        setData(prev => (page === 1 ? rows : [...prev, ...rows]));
        setHasMore(rows.length === PAGE_SIZE);
      } catch (err) {
        if (rid !== requestId.current) return;
        console.error('[useShareableOrders] fetch failed:', err);
        if (page === 1) setData([]);
        setHasMore(false);
      } finally {
        if (rid === requestId.current) {
          setIsInitializing(false);
          setIsLoadingMore(false);
        }
      }
    },
    [role, currentUserId, partnerId],
  );

  useEffect(() => {
    setPageNo(1);
    void fetchPage(1);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isInitializing || isLoadingMore || !hasMore) return;
    const next = pageNo + 1;
    setPageNo(next);
    void fetchPage(next);
  }, [isInitializing, isLoadingMore, hasMore, pageNo, fetchPage]);

  return { data, isInitializing, isLoadingMore, hasMore, loadMore };
}

async function fetchSellerOrders(
  currentUserId: string,
  buyerId: string,
  page: number,
): Promise<RichOrderItem[]> {
  const api = withClientAPI(SearchApi);
  const res = await api.searchApiControllerSearchSellerOrders({
    storeId: currentUserId,
    buyerId,
    pageNo: page,
    pageSize: PAGE_SIZE,
    sortBy: SearchApiControllerSearchSellerOrdersSortByEnum.CreatedAt,
    order: SearchApiControllerSearchSellerOrdersOrderEnum.Desc,
    groupStatus: [
      SearchApiControllerSearchSellerOrdersGroupStatusEnum.Master,
      SearchApiControllerSearchSellerOrdersGroupStatusEnum.Customer,
    ],
  });
  return res.data?.rows ?? [];
}

async function fetchBuyerOrders(
  currentUserId: string,
  sellerId: string,
  page: number,
): Promise<RichOrderItem[]> {
  const token = await auth().currentUser?.getIdToken();
  const params = new URLSearchParams({
    pageNo: String(page),
    pageSize: String(PAGE_SIZE),
    allOrders: 'true',
    sortBy: 'createdAt',
    order: 'desc',
    sellerId,
  });
  params.append('groupStatus', 'master');
  params.append('groupStatus', 'customer');
  const res = await fetch(
    `${config.apiEndpoint}/v1/users/${encodeURIComponent(
      currentUserId,
    )}/orders:search?${params}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
  if (!res.ok) throw new Error(`Orders fetch failed: ${res.status}`);
  const body = (await res.json()) as OrdersSearchResponseLike;
  const rows = body.data?.rows ?? [];
  // Defensive: if the endpoint ignores `sellerId`, keep only this
  // conversation's seller so the picker never shows unrelated orders.
  return rows.filter(row => row.sellerId === sellerId);
}
